import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import StudentDashboard from "@/pages/student/StudentDashboard";
import InstructorDashboard from "@/pages/instructor/InstructorDashboard";
import InstructorTeamView from "@/pages/instructor/InstructorTeamView";
import GameLobby from "@/pages/GameLobby";
import SuperadminDashboard from "@/pages/SuperadminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const isValidJWT = (token) => {
  if (!token || typeof token !== "string") return false;
  const parts = token.split(".");
  if (parts.length !== 3) return false;
  try {
    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) return false;
    return true;
  } catch {
    return false;
  }
};

// Token storage: using sessionStorage (clears on tab close) instead of localStorage
// for reduced XSS exposure. Token is also validated before use.
const readStoredToken = () => {
  try {
    const stored = sessionStorage.getItem("skyboard_token");
    return isValidJWT(stored) ? stored : null;
  } catch {
    return null;
  }
};

const saveToken = (token) => {
  try { sessionStorage.setItem("skyboard_token", token); } catch (e) { console.error("Token save failed:", e.message); }
};

const clearToken = () => {
  try { sessionStorage.removeItem("skyboard_token"); } catch { /* noop */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(readStoredToken);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    saveToken(access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, role, invite_code, consent = {}) => {
    const response = await axios.post(`${API}/auth/register`, {
      name, email, password, role, invite_code,
      accepted_terms: consent.accepted_terms === true,
      marketing_consent: consent.marketing_consent === true,
    });
    const { access_token, user: userData } = response.data;
    saveToken(access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    clearToken();
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-sky-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === "superadmin") return <Navigate to="/admin" replace />;
    return <Navigate to={user.role === "instructor" ? "/instructor" : "/games"} replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          
          {/* Student Routes */}
          <Route path="/games" element={
            <ProtectedRoute allowedRoles={["student"]}>
              <GameLobby />
            </ProtectedRoute>
          } />
          <Route path="/game/:gameId" element={
            <ProtectedRoute allowedRoles={["student"]}>
              <StudentDashboard />
            </ProtectedRoute>
          } />
          
          {/* Instructor Routes */}
          <Route path="/instructor" element={
            <ProtectedRoute allowedRoles={["instructor", "superadmin"]}>
              <InstructorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/instructor/game/:gameId" element={
            <ProtectedRoute allowedRoles={["instructor", "superadmin"]}>
              <InstructorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/instructor/view-team/:gameId/:teamId" element={
            <ProtectedRoute allowedRoles={["instructor", "superadmin"]}>
              <InstructorTeamView />
            </ProtectedRoute>
          } />
          
          {/* Superadmin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={["superadmin"]}>
              <SuperadminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Redirect */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

const RootRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-sky-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role === "superadmin") return <Navigate to="/admin" replace />;
  return <Navigate to={user.role === "instructor" ? "/instructor" : "/games"} replace />;
};

export default App;
